import React from 'react';
import { Skeleton } from '../../../components/ui/skeleton';
import { EntityRecordSkeleton } from '../../../../components/workspaces/EntityWorkspacePrimitives';
import { SegmentPageBar, type SegmentCrumb } from './SegmentPageBar';

const stack = 'flex flex-col gap-[var(--cs-segment-row-gap)]';
const panel = `${stack} rounded-[var(--cs-input-radius)] border border-edge bg-paper-light p-[var(--cs-segment-panel-padding)]`;
const title = 'h-[var(--cs-dashboard-skeleton-title-height)] w-1/2';
const control = 'h-[var(--cs-dashboard-shell-toolbar-height)]';

export function SegmentPeopleSkeleton({ rows = 5, label = 'Loading people', avatar = true }: { rows?: number; label?: string; avatar?: boolean }) {
  return (
    <div role="status" aria-label={label} aria-busy="true">
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} aria-hidden="true" className="flex flex-wrap items-center gap-[var(--cs-segment-row-gap)] border-b border-edge py-[var(--cs-segment-row-padding)]">
          {avatar && <Skeleton className="size-[var(--cs-segment-avatar-size)] shrink-0 rounded-full" />}
          <div className={`flex min-w-0 flex-1 flex-col gap-[var(--cs-audience-heading-gap)] ${avatar ? 'basis-[var(--cs-segment-person-mobile-basis)] sm:basis-0' : ''}`}>
            <Skeleton className="w-1/2" />
            <Skeleton className="w-3/4" />
          </div>
          <Skeleton className="w-1/4" />
        </div>
      ))}
    </div>
  );
}

export function SegmentPreviewSkeleton() {
  return (
    <div role="status" aria-label="Counting segment" aria-busy="true" className={stack}>
      <Skeleton className="h-[var(--cs-dashboard-skeleton-stat-height)] w-1/2" />
      <div className="flex gap-[var(--cs-segment-row-gap)]">
        {[0, 1].map((index) => <div key={index} className={`${panel} min-w-0 flex-1`}><Skeleton className={control} /><Skeleton /></div>)}
      </div>
    </div>
  );
}

export function SegmentTemplateSkeleton() {
  return <div className={panel} aria-hidden="true"><Skeleton className="w-1/2" /><Skeleton className={control} /><Skeleton /><Skeleton className="w-3/4" /><Skeleton className="w-1/2" /></div>;
}

export function SegmentEntrySkeleton({ crumbs }: { crumbs: SegmentCrumb[] }) {
  return (
    <div className="flex h-full min-h-0 flex-col" role="status" aria-label="Loading new segment" aria-busy="true">
      <SegmentPageBar crumbs={crumbs} />
      <div className="flex min-h-0 flex-1 flex-col gap-[var(--cs-segment-section-gap)] overflow-y-auto px-[var(--cs-segment-page-mobile-inset)] pb-[var(--cs-segment-content-bottom)] pt-[var(--cs-segment-entry-content-top)] md:px-[var(--cs-segment-page-inset)]" aria-hidden="true">
        <Skeleton className={title} /><Skeleton className="w-3/4" />
        <div className="flex flex-col gap-[var(--cs-segment-section-gap)] lg:flex-row">
          <div className={`${panel} shrink-0 lg:w-[var(--cs-segment-entry-card-width)]`}><Skeleton className={title} /><Skeleton /><Skeleton className="h-[var(--cs-dashboard-skeleton-block-height)]" /><div className="flex flex-col gap-[var(--cs-segment-action-gap)]"><Skeleton className={control} /><Skeleton className={control} /></div></div>
          <div className="grid min-w-0 flex-1 gap-[var(--cs-segment-row-gap)] sm:grid-cols-2 xl:grid-cols-3">{Array.from({ length: 6 }, (_, index) => <SegmentTemplateSkeleton key={index} />)}</div>
        </div>
      </div>
    </div>
  );
}

export function SegmentWorkspaceSkeleton({ mode, crumbs }: { mode: 'view' | 'edit' | 'create'; crumbs: SegmentCrumb[] }) {
  if (mode === 'view') return <div className="flex h-full min-h-0 flex-col"><SegmentPageBar crumbs={crumbs} /><EntityRecordSkeleton label="segment" /></div>;
  return (
    <div className="flex h-full min-h-0 flex-col" role="status" aria-label="Loading segment builder" aria-busy="true">
      <SegmentPageBar crumbs={crumbs}>
        {[0, 1].map((index) => <Skeleton key={index} className={`${control} w-[var(--cs-dashboard-skeleton-label-width)]`} />)}
      </SegmentPageBar>
      <div className="min-h-0 flex-1 overflow-y-auto px-[var(--cs-segment-page-mobile-inset)] pb-[var(--cs-segment-content-bottom)] pt-[var(--cs-segment-content-top)] md:px-[var(--cs-segment-page-inset)]" aria-hidden="true">
        <div className="flex flex-col gap-[var(--cs-segment-section-gap)]">
          <div className="flex flex-col items-start gap-[var(--cs-segment-section-gap)] lg:flex-row">
            <div className="flex w-full min-w-0 flex-1 flex-col gap-[var(--cs-segment-section-gap)]">
              <>
                <Skeleton className={title} />
                <Skeleton className="w-1/2" />
                {[0, 1, 2].map((index) => <div key={index} className={stack}><Skeleton className="w-1/2" /><div className={panel}><Skeleton className="h-[var(--cs-dashboard-skeleton-block-height)]" /></div></div>)}
              </>
            </div>
            <aside className={`${panel} w-full shrink-0 lg:w-[var(--cs-segment-preview-width)]`}>
              <SegmentPreviewSkeleton />
            </aside>
          </div>
        </div>
      </div>
    </div>
  );
}
